import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { formatPrice } from "../utils/format";
import { TrendingUp, ShoppingBag, Users, Package, ArrowUp, ArrowDown } from "lucide-react";

function BarChart({ data, label, color = "var(--brand)" }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 140, marginBottom: 8 }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, height: "100%", justifyContent: "flex-end" }}>
            <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600 }}>
              {d.value > 0 ? (label === "revenue" ? formatPrice(d.value).replace("₦", "₦") : d.value) : ""}
            </span>
            <div className="chart-bar" style={{ width: "100%", background: color, borderRadius: "4px 4px 0 0", opacity: 0.85,
              height: `${Math.max(4, (d.value / max) * 110)}px`, transition: "height 0.6s cubic-bezier(0.34,1.56,0.64,1)" }} />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, textAlign: "center", fontSize: 11, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.label}</div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, change, color }) {
  const up = change >= 0;
  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 6 }}>{label}</p>
          <p style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 700 }}>{value}</p>
          {change !== undefined && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 6, fontSize: 13, color: up ? "var(--success)" : "var(--danger)" }}>
              {up ? <ArrowUp size={13} /> : <ArrowDown size={13} />}
              <span>{Math.abs(change)}% vs last month</span>
            </div>
          )}
        </div>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: color + "20", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={20} color={color} />
        </div>
      </div>
    </div>
  );
}

export default function AdminAnalyticsPage() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getDocs(query(collection(db, "orders"), orderBy("createdAt", "desc"))),
      getDocs(collection(db, "products")),
      getDocs(collection(db, "users")),
    ]).then(([oSnap, pSnap, uSnap]) => {
      setOrders(oSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setProducts(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setUsers(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, []);

  // Build last 7 days revenue data
  function getLast7DaysRevenue() {
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(); d.setDate(d.getDate() - (6 - i));
      return { date: d, label: d.toLocaleDateString("en-NG", { weekday: "short" }), value: 0 };
    });
    orders.forEach(o => {
      if (!o.createdAt) return;
      const d = o.createdAt.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
      const idx = days.findIndex(day => day.date.toDateString() === d.toDateString());
      if (idx >= 0) days[idx].value += o.total || 0;
    });
    return days;
  }

  // Last 6 months
  function getLast6MonthsRevenue() {
    const months = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(); d.setMonth(d.getMonth() - (5 - i));
      return { month: d.getMonth(), year: d.getFullYear(), label: d.toLocaleDateString("en-NG", { month: "short" }), value: 0 };
    });
    orders.forEach(o => {
      if (!o.createdAt) return;
      const d = o.createdAt.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
      const idx = months.findIndex(m => m.month === d.getMonth() && m.year === d.getFullYear());
      if (idx >= 0) months[idx].value += o.total || 0;
    });
    return months;
  }

  // Top products by order frequency
  function getTopProducts() {
    const freq = {};
    orders.forEach(o => (o.items || []).forEach(item => { freq[item.name] = (freq[item.name] || 0) + item.qty; }));
    return Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }

  // Category breakdown
  function getCategoryBreakdown() {
    const cats = {};
    orders.forEach(o => (o.items || []).forEach(item => {
      if (item.category) cats[item.category] = (cats[item.category] || 0) + (item.price * item.qty);
    }));
    return Object.entries(cats).sort((a, b) => b[1] - a[1]).slice(0, 6)
      .map(([label, value]) => ({ label: label.split(" ")[0], value }));
  }

  const totalRevenue = orders.reduce((s, o) => s + (o.total || 0), 0);
  const totalOrders = orders.length;
  const avgOrder = totalOrders ? Math.round(totalRevenue / totalOrders) : 0;
  const businessUsers = users.filter(u => u.role === "business").length;
  const activeProducts = products.filter(p => p.status === "active").length;

  if (loading) return <div className="loader" style={{ minHeight: 400 }}><div className="spinner" /></div>;

  return (
    <div style={{ padding: 32 }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: "var(--font-head)", fontSize: 24, fontWeight: 700 }}>Analytics</h1>
        <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 2 }}>Store performance overview</p>
      </div>

      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", gap: 16, marginBottom: 28 }}>
        <StatCard icon={TrendingUp} label="Total Revenue" value={formatPrice(totalRevenue)} color="var(--brand)" />
        <StatCard icon={ShoppingBag} label="Total Orders" value={totalOrders} color="var(--accent)" />
        <StatCard icon={TrendingUp} label="Avg Order Value" value={formatPrice(avgOrder)} color="var(--success)" />
        <StatCard icon={Package} label="Active Products" value={activeProducts} color="var(--gold)" />
        <StatCard icon={Users} label="Business Sellers" value={businessUsers} color="#8B5CF6" />
      </div>

      {/* Charts row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap: 20, marginBottom: 20 }}>
        <div className="card" style={{ padding: 24 }}>
          <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 16, marginBottom: 20 }}>Revenue — Last 7 Days</h3>
          <BarChart data={getLast7DaysRevenue()} label="revenue" color="var(--brand)" />
        </div>
        <div className="card" style={{ padding: 24 }}>
          <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 16, marginBottom: 20 }}>Revenue — Last 6 Months</h3>
          <BarChart data={getLast6MonthsRevenue()} label="revenue" color="var(--accent)" />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap: 20 }}>
        {/* Top products */}
        <div className="card" style={{ padding: 24 }}>
          <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 16, marginBottom: 20 }}>Top Products by Units Sold</h3>
          {getTopProducts().length === 0 ? (
            <p style={{ color: "var(--text-muted)", fontSize: 14 }}>No sales data yet.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {getTopProducts().map(([name, qty], i) => (
                <div key={name} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 24, height: 24, borderRadius: "50%", background: i === 0 ? "var(--gold)" : "var(--surface)", color: i === 0 ? "#fff" : "var(--text-muted)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
                  <span className="badge badge-default">{qty} sold</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Category revenue */}
        <div className="card" style={{ padding: 24 }}>
          <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 16, marginBottom: 20 }}>Revenue by Category</h3>
          {getCategoryBreakdown().length === 0 ? (
            <p style={{ color: "var(--text-muted)", fontSize: 14 }}>No sales data yet.</p>
          ) : (
            <BarChart data={getCategoryBreakdown()} label="revenue" color="#8B5CF6" />
          )}
        </div>
      </div>

      {/* Order status breakdown */}
      <div className="card" style={{ padding: 24, marginTop: 20 }}>
        <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 16, marginBottom: 20 }}>Order Status Breakdown</h3>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {["confirmed","processing","shipped","delivered","cancelled"].map(status => {
            const count = orders.filter(o => o.status === status).length;
            const pct = totalOrders ? Math.round((count / totalOrders) * 100) : 0;
            const colors = { confirmed: "var(--accent)", processing: "var(--warning)", shipped: "var(--gold)", delivered: "var(--success)", cancelled: "var(--danger)" };
            return (
              <div key={status} style={{ flex: 1, minWidth: 120 }}>
                <div style={{ fontSize: 13, color: "var(--text-muted)", textTransform: "capitalize", marginBottom: 4 }}>{status}</div>
                <div style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 700, color: colors[status] }}>{count}</div>
                <div style={{ height: 4, background: "var(--border)", borderRadius: 2, marginTop: 6 }}>
                  <div style={{ height: "100%", width: pct + "%", background: colors[status], borderRadius: 2, transition: "width 0.8s" }} />
                </div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>{pct}%</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
